import { useCallback, useEffect, useState } from "react";
import { api } from "../api.js";

/**
 * Кадр 5 на живых данных. Пока порог n не набран — на экране расстояние до сигнала,
 * а не число: значение при малом n было бы статистическим театром. У каждого субнавыка
 * видна его формула и версия — это формула над событиями, а не оценка.
 *
 * ⚠️ Субнавыки собраны в ОДНУ стеклянную панель с разделителями внутри, а не в четыре
 * карточки, как в эталоне. В эталоне их намеренно оставили четырьмя, чтобы проблема была
 * видна: с калибровкой и панелью вкладок в кадре получалось шесть стеклянных слоёв при
 * лимите в четыре. Каждый слой — отдельный проход композитора.
 */

type SkillMetric = {
  key: string;
  title: string;
  formula: string;
  formula_version: string;
  n: number;
  threshold_n: number;
  sufficient: boolean;
  value: number | null;
  unit: string | null;
  detail: Record<string, number | null>;
};

type SkillResponse = { metrics: SkillMetric[] };

/** Строка снимка: ровно то, что каждую ночь пишет рефлексия. */
type Snapshot = {
  computed_day: string;
  value: number | null;
  n: number;
  sufficient: number;
  formula_version: string;
};

/**
 * Сдвиг — сутки, в которые в метрике что-то изменилось. Считает их сервер: сравнимость
 * значений по разные стороны смены формулы — решение, а не рендер.
 */
type Shift =
  | { day: string; kind: "value"; from: number | null; to: number | null }
  | { day: string; kind: "n"; from: number; to: number }
  | { day: string; kind: "signal"; n: number; on: boolean }
  | { day: string; kind: "formula"; from: string; to: string };

type HistoryResponse = { history: Snapshot[]; shifts: Shift[] };
type Calibration = {
  binary: { n: number; brier: number | null; overconfidence: number | null };
  dates: { n: number; median_signed_error_days: number | null; inside_interval_rate: number | null };
};

export function SkillScreen(): React.JSX.Element {
  const [skill, setSkill] = useState<SkillResponse | null>(null);
  const [calibration, setCalibration] = useState<Calibration | null>(null);
  const [history, setHistory] = useState<Record<string, HistoryResponse>>({});
  const [snapping, setSnapping] = useState(false);

  const load = useCallback(async () => {
    const profile = await api.get<SkillResponse>("/api/skill");
    setSkill(profile);
    const loaded = await Promise.all(
      profile.metrics.map(async (metric) => [metric.key, await api.get<HistoryResponse>(`/api/skill/${metric.key}/history`)] as const)
    );
    setHistory(Object.fromEntries(loaded));
  }, []);

  useEffect(() => {
    void load();
    void api.get<Calibration>("/api/calibration").then(setCalibration);
  }, [load]);

  /**
   * Снимок руками. Второй снимок за те же сутки не добавляет точку, а обновляет её:
   * иначе тридцать нажатий вытеснили бы месяц истории сегодняшним днём.
   */
  const snapNow = async () => {
    setSnapping(true);
    try {
      await api.post("/api/skill/snapshot");
      await load();
    } finally {
      setSnapping(false);
    }
  };

  if (!skill) return <p className="empty">считаю…</p>;

  const silent = skill.metrics.filter((metric) => !metric.sufficient).length;
  const snapshots = Object.values(history).reduce((max, item) => Math.max(max, item.history.length), 0);

  return (
    <>
      <header className="head">
        <h1>Навык</h1>
      </header>
      <p className="state-note">
        {silent === skill.metrics.length ? (
          <>
            <b>Ни один субнавык пока не заговорил.</b> Значение появляется только при своём пороге n —
            иначе это красивое число, посчитанное по двум случаям.
          </>
        ) : (
          <>
            Значения показываются только там, где набран порог n. Молчащих субнавыков: <b>{silent}</b>.
          </>
        )}
      </p>

      <div className="sec">Субнавыки</div>
      <section className="subskills">
        {skill.metrics.map((metric) => (
          <div className="subskill" key={metric.key}>
            <div className="top">
              <span className="name">{metric.title}</span>
              <span className="ver">формула {metric.formula_version}</span>
            </div>
            <div className="formula">{metric.formula}</div>

            <div className="cells">
              {Array.from({ length: metric.threshold_n }, (_, index) => (
                <i key={index} className={index < metric.n ? "on" : ""} />
              ))}
            </div>
            <div className="gap">
              {metric.sufficient ? (
                <span className="need">
                  <b>
                    {metric.value}
                    {metric.unit ? ` ${metric.unit}` : ""}
                  </b>
                </span>
              ) : (
                <span className="need">
                  до сигнала: ещё <b>{metric.threshold_n - metric.n}</b>
                </span>
              )}
              <span className="of">
                {metric.n} / {metric.threshold_n}
              </span>
            </div>

            <Raw detail={metric.detail} />
            <History metric={metric} data={history[metric.key]} />
          </div>
        ))}
      </section>
      <p className="snapline">
        <span>
          {snapshots === 0 ? "снимков пока нет" : `${snapshots} снимков`} · пишет ночная рефлексия
        </span>
        <button className="linklike" onClick={() => void snapNow()} disabled={snapping}>
          {snapping ? "снимаю…" : "снять сейчас"}
        </button>
      </p>

      {calibration && (
        <>
          <div className="sec">Калибровка</div>
          <section className="rows">
            <div className="row">
              <span className="two-line">
                Бинарные прогнозы
                <span className="hint">
                  {calibration.binary.n === 0
                    ? "разрешённых пока нет"
                    : `Brier ${calibration.binary.brier} · смещение ${calibration.binary.overconfidence} п.п.`}
                </span>
              </span>
              <span className="sub">n = {calibration.binary.n}</span>
            </div>
            <div className="row">
              <span className="two-line">
                Прогнозы сроков
                <span className="hint">
                  {calibration.dates.n === 0
                    ? "разрешённых пока нет"
                    : `медиана ошибки ${calibration.dates.median_signed_error_days} дн. · в интервал ${calibration.dates.inside_interval_rate ?? "—"}%`}
                </span>
              </span>
              <span className="sub">n = {calibration.dates.n}</span>
            </div>
          </section>
        </>
      )}

      <p className="invariant">
        Все числа на этом экране считает код по формулам выше. Версия формулы хранится вместе
        со значением: если формула изменится, старые снимки не станут задним числом другими.
      </p>
    </>
  );
}

/** Машинные ключи наружу не показываем: подпись читает владелец, а не разработчик. */
const DETAIL_LABEL: Record<string, string> = {
  median_signed_error_days: "медиана ошибки, дней",
  inside_interval_rate: "попаданий в интервал, %",
  inside_interval_target: "эталон интервала, %",
  n_with_interval: "прогнозов с интервалом",
  median_days_to_return: "медиана возврата, дней",
  without_return: "срывов без возврата",
  without_return_rate: "доля без возврата, %",
  killed_in_cooling: "убито в отлёжке",
  abandoned_early: "брошено в первые 14 дней",
  matured: "целей дожило до оценки",
  killed_share: "доля отсева, %",
  abandoned_early_share: "доля раннего отказа, %"
};

/** Сырые наблюдения показываем и до порога: факт со своим n — это не оценка навыка. */
function Raw({ detail }: { detail: Record<string, number | null> }): React.JSX.Element | null {
  const entries = Object.entries(detail).filter(([, value]) => value !== null && value !== undefined);
  if (entries.length === 0) return null;
  return (
    <div className="raw">
      {entries.map(([key, value]) => (
        <span className="raw-item" key={key}>
          <span className="k">{DETAIL_LABEL[key] ?? key}</span>
          <span className="v">{value}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * История снимков. Форма зависит от состояния субнавыка — так решил владелец 07.09:
 *
 *   — **заговорил** (порог набран) → линия значения. Смотреть на движение числа;
 *   — **молчит** → лента сдвигов. При N=1 значение меняется раз в неделю-две, и линия
 *     из тридцати точек, где двадцать восемь одинаковых, выглядит как данные, не будучи
 *     ими. Список сдвигов честно короткий, а если он пуст — так и написано.
 */
function History({ metric, data }: { metric: SkillMetric; data?: HistoryResponse }): React.JSX.Element | null {
  if (!data || data.history.length === 0) return null;
  return metric.sufficient ? <Line metric={metric} data={data} /> : <Shifts shifts={data.shifts} days={data.history.length} />;
}

/**
 * ⚠️ Рисуется ТОЛЬКО отрезок текущей формулы. Соседний отрезок другой версии — другая
 * линейка: положить их на одну шкалу значило бы предложить сравнить несравнимое, а смену
 * формулы выдать за движение метрики. Что до отрезка была другая версия, сказано текстом.
 */
function Line({ metric, data }: { metric: SkillMetric; data: HistoryResponse }): React.JSX.Element {
  const ordered = [...data.history].reverse(); // снимки идут свежими сверху
  const version = ordered[ordered.length - 1]?.formula_version;
  const segment: Snapshot[] = [];
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const snapshot = ordered[index]!;
    if (snapshot.formula_version !== version || snapshot.value === null) break;
    segment.unshift(snapshot);
  }

  const formula = data.shifts.find((shift) => shift.kind === "formula");
  const signal = data.shifts.find((shift) => shift.kind === "signal");
  const first = segment[0]?.value ?? null;
  const last = segment[segment.length - 1]?.value ?? null;
  const unit = metric.unit ? ` ${metric.unit}` : "";

  return (
    <div className="trend">
      {segment.length < 2 ? (
        <p className="one-point">одна точка — линия появится со второго снимка на этой формуле</p>
      ) : (
        <svg className="spark" viewBox="0 0 320 34" preserveAspectRatio="none" aria-hidden="true">
          <polyline points={polyline(segment)} fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={320 - 6} cy={pointY(segment, segment.length - 1)} r="2.75" />
        </svg>
      )}
      <div className="under">
        <span>
          {formula
            ? `формула ${formula.from} → ${formula.to} ${dayRu(formula.day)}, прошлое не пересчитано`
            : signal
              ? `сигнал с ${dayRu(signal.day)}`
              : `${segment.length} снимков на этой формуле`}
        </span>
        <span>
          {first !== null && last !== null && first !== last ? (
            <b>
              {first} → {last}
              {unit}
            </b>
          ) : (
            "без движения"
          )}
        </span>
      </div>
    </div>
  );
}

function Shifts({ shifts, days }: { shifts: Shift[]; days: number }): React.JSX.Element {
  if (shifts.length === 0) {
    return (
      <div className="shifts">
        <div className="shift flat">
          <span className="what">за {days} снимков ни одного сдвига</span>
        </div>
      </div>
    );
  }
  return (
    <div className="shifts">
      {shifts.slice(0, 3).map((shift) => (
        <div className="shift" key={`${shift.day}-${shift.kind}`}>
          <span className="when">{dayRu(shift.day)}</span>
          <span className="what">{shiftText(shift)}</span>
        </div>
      ))}
    </div>
  );
}

/** Машинный ключ вида `formula` в текст для владельца не попадает — здесь его подпись. */
function shiftText(shift: Shift): React.JSX.Element {
  switch (shift.kind) {
    case "signal":
      return (
        <>
          {shift.on ? "сигнал появился" : "сигнал пропал"}, <b>n = {shift.n}</b>
        </>
      );
    case "formula":
      return (
        <>
          формула{" "}
          <b>
            {shift.from} → {shift.to}
          </b>
          , прошлое не пересчитано
        </>
      );
    case "value":
      return (
        <>
          значение{" "}
          <b>
            {shift.from ?? "—"} → {shift.to ?? "—"}
          </b>
        </>
      );
    default:
      return (
        <>
          набор{" "}
          <b>
            {shift.from} → {shift.to}
          </b>
        </>
      );
  }
}

/**
 * ⚠️ `computed_day` — уже сутки владельца, строка «ГГГГ-ММ-ДД». Разбирать её через
 * `new Date(строка)` нельзя: такая строка читается как полночь UTC, и в поясе устройства
 * шестое сентября показалось бы пятым. Собираем локальную дату из чисел.
 */
function dayRu(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return day;
  return new Date(year, month - 1, date).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

/** Ось X — по датам снимков, а не по их номеру: пропущенная ночь не должна выглядеть темпом. */
function pointX(segment: Snapshot[], index: number): number {
  const start = Date.parse(segment[0]!.computed_day);
  const end = Date.parse(segment[segment.length - 1]!.computed_day);
  const at = Date.parse(segment[index]!.computed_day);
  const span = end - start;
  return 6 + (span > 0 ? ((at - start) / span) * (320 - 12) : index === 0 ? 0 : 320 - 12);
}

function pointY(segment: Snapshot[], index: number): number {
  const values = segment.map((snapshot) => snapshot.value ?? 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const value = values[index]!;
  // Плоский отрезок кладём посередине, а не прижимаем к краю: «ничего не менялось» —
  // это ровная линия, а не дно шкалы.
  return max === min ? 17 : 30 - ((value - min) / (max - min)) * 26;
}

function polyline(segment: Snapshot[]): string {
  return segment.map((_, index) => `${pointX(segment, index).toFixed(1)},${pointY(segment, index).toFixed(1)}`).join(" ");
}
